import * as React from "react";
import {
  File as FileIcon,
  FileText,
  FileImage,
  FileAudio,
  FileVideo,
  FileSpreadsheet,
  FileCode,
  FileJson,
  FileArchive,
} from "lucide-react";

/**
 * fileUtils — pure, dependency-free helpers for the SourceIngestion family.
 *
 * Ported from Creator Studio's `vault/upload/utils/fileUtils.tsx` and
 * `UrlInputView.tsx`. Two concerns live here:
 *
 *   1. File presentation — `formatFileSize`, `getFileIcon` (by mime),
 *      `getFileIconForName` (by extension), and `createUploadFile` (builds a
 *      queue entry). The donor hardcoded per-type icon colors (`text-red-500`
 *      etc.); we keep the icon-by-type mapping but render in
 *      `text-muted-foreground` so the row chrome owns color — status tint and
 *      brand tokens do the talking, not a rainbow of file icons.
 *
 *   2. URL validation — `validateUrls` + `parseUrlLines`, the multi-line
 *      paste-a-bunch-of-links logic. This is ported VERBATIM (same regex,
 *      same line split + trim + filter) because it is battle-tested in the
 *      donor and pulling in a form/validation lib for one regex would be
 *      structure unjustified by a force.
 *
 * SSR-safe: no `window` / `document` access at module top or inside any
 * helper. `createUploadFile` uses `crypto.randomUUID()` when available and
 * falls back to a time+random id, both of which are safe on the server.
 */

/** Lifecycle of one queued upload. `idle` is the pre-start resting state. */
export type UploadStatus = "idle" | "uploading" | "success" | "error";

/**
 * A single entry in the upload queue. The consumer owns the array and mutates
 * `status` / `progress` as its own transport resolves — the family never
 * fetches. `icon` is optional: `FileItem` derives one from `name` when absent.
 */
export interface UploadFile {
  /** Stable unique id — React key + the handle passed to remove / retry callbacks. */
  id: string;
  /** Display name (usually the original `File.name`). */
  name: string;
  /** Human-readable size, e.g. "2.4 MB". Produced by `formatFileSize`. */
  size: string;
  /** MIME type (`File.type`) — drives `getFileIcon`. */
  type: string;
  /** Current lifecycle state. */
  status: UploadStatus;
  /** 0-100 determinate progress, shown while `status === "uploading"`. */
  progress?: number;
  /** Optional pre-rendered icon. When omitted the row derives one from `name`. */
  icon?: React.ReactNode;
  /** Optional human-readable error, surfaced under the row when `status === "error"`. */
  error?: string;
}

/**
 * Map a MIME type to a lucide icon element. Color is intentionally
 * `text-muted-foreground` (not the donor's per-type rainbow) so the row's
 * status tint stays the dominant signal. Pass `className` to resize.
 */
export function getFileIcon(mimeType: string, className = "size-5"): React.ReactElement {
  const type = (mimeType || "").toLowerCase();
  const cls = `${className} text-muted-foreground`;
  if (type.includes("image")) return <FileImage className={cls} aria-hidden />;
  if (type.includes("audio")) return <FileAudio className={cls} aria-hidden />;
  if (type.includes("video")) return <FileVideo className={cls} aria-hidden />;
  if (type.includes("pdf")) return <FileText className={cls} aria-hidden />;
  if (type.includes("json")) return <FileJson className={cls} aria-hidden />;
  if (type.includes("zip") || type.includes("compressed")) return <FileArchive className={cls} aria-hidden />;
  if (type.includes("spreadsheet") || type.includes("excel") || type.includes("csv"))
    return <FileSpreadsheet className={cls} aria-hidden />;
  if (type.includes("html") || type.includes("xml")) return <FileCode className={cls} aria-hidden />;
  if (type.includes("text")) return <FileText className={cls} aria-hidden />;
  return <FileIcon className={cls} aria-hidden />;
}

const EXTENSION_MIME: Record<string, string> = {
  pdf: "application/pdf",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
  html: "text/html",
  htm: "text/html",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  mp4: "video/mp4",
  mov: "video/quicktime",
  zip: "application/zip",
};

/**
 * Derive an icon from a filename's extension (the donor's `FileUploadList`
 * picked icons by extension, not MIME). Falls back to the generic file icon.
 */
export function getFileIconForName(name: string, className = "size-4"): React.ReactElement {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return getFileIcon(EXTENSION_MIME[ext] ?? "", className);
}

/**
 * Human-readable byte size. B under 1 KiB, KB under 1 MiB, MB above.
 * Ported verbatim from the donor (same thresholds, same `toFixed(1)`).
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** Crypto-backed id with a SSR-safe fallback. */
function generateId(): string {
  if (typeof globalThis !== "undefined" && globalThis.crypto?.randomUUID) {
    return `file-${globalThis.crypto.randomUUID()}`;
  }
  return `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Build an `UploadFile` queue entry from a browser `File`, in the initial
 * `uploading` state with a derived icon. The donor named this
 * `contentFileUpload`; renamed to `createUploadFile` for a verb-noun shape
 * that reads as "construct a record", not "perform an upload" (the family
 * never uploads).
 */
export function createUploadFile(file: File): UploadFile {
  return {
    id: generateId(),
    name: file.name,
    size: formatFileSize(file.size),
    type: file.type,
    status: "uploading",
    progress: 0,
    icon: getFileIcon(file.type),
  };
}

/* ----------------------------------------------------------------------------
 * URL validation — ported VERBATIM from the donor's UrlInputView.
 * ------------------------------------------------------------------------- */

/** The donor's URL pattern — `http(s)://` then at least `x.y`. Kept exact. */
export const URL_PATTERN = /^https?:\/\/.+\..+/i;

/**
 * Split a multi-line URL blob into trimmed, non-empty lines (the donor's
 * `split("\n").filter(...)` shape). Use for the live "{n} links detected"
 * counter and to feed `onSubmit`.
 */
export function parseUrlLines(urlText: string): string[] {
  return urlText
    .split("\n")
    .map((u) => u.trim())
    .filter((u) => u !== "");
}

/** Result of `validateUrls`: ok, plus the first offending line when invalid. */
export interface UrlValidationResult {
  /** True when every non-empty line matches `URL_PATTERN`. */
  valid: boolean;
  /** Inline error message, mirroring the donor's exact copy. Empty when valid. */
  error: string;
}

/**
 * Validate a multi-line URL blob. Ported verbatim from the donor:
 *   - empty / whitespace-only -> "Please enter at least one URL"
 *   - any line failing `URL_PATTERN` -> `Invalid URL: <line>`
 *   - otherwise valid.
 *
 * Returns a result object (instead of the donor's `setState` side effect) so
 * the panel stays presentational and the validator is unit-testable.
 */
export function validateUrls(urlText: string): UrlValidationResult {
  if (!urlText.trim()) {
    return { valid: false, error: "Please enter at least one URL" };
  }
  for (const url of parseUrlLines(urlText)) {
    if (!URL_PATTERN.test(url)) {
      return { valid: false, error: `Invalid URL: ${url}` };
    }
  }
  return { valid: true, error: "" };
}

/**
 * Auto-derive a filename from pasted text — first 30 chars, whitespace to
 * underscore, non-alphanumerics stripped, `"untitled"` fallback. Ported
 * verbatim from the donor's `useAddToVault` effect.
 */
export function autoFilenameFromText(text: string): string {
  const snippet = text
    .substring(0, 30)
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "");
  return snippet || "untitled";
}
